/**
 * Analytics Beacon — HTTP beacon for sending analytics events to backend (#589)
 *
 * Uses navigator.sendBeacon() to reliably deliver analytics events,
 * even during page unload. Events are buffered and flushed periodically.
 *
 * This works independently of GTM — events are saved directly to
 * browser_control_events table via POST /api/public/analytics/events.
 *
 * Flow:
 * 1. Widget calls initBeacon() on mount
 * 2. analyticsBridge calls trackEvent() for each sg_ event
 * 3. Events accumulate in buffer
 * 4. Buffer is flushed every FLUSH_INTERVAL_MS or on page hide/unload
 * 5. Backend validates frontendToken → saves to DB
 */

import { getOrCreateBrowsingSessionId } from './sessionManager';

const FLUSH_INTERVAL_MS = 10_000; // 10 seconds
const MAX_BUFFER_SIZE = 50;
const ENDPOINT_PATH = '/api/public/analytics/events';

/** @type {AnalyticsEvent[]} */
let eventBuffer = [];

/** @type {number|null} */
let flushIntervalId = null;

/** @type {string|null} */
let apiUrl = null;

/** @type {string|null} */
let frontendToken = null;

/** @type {string|null} */
let visitorId = null;

/** @type {string|null} */
let currentSessionId = null;

/** @type {boolean} */
let initialized = false;

/** @type {string} Last tracked URL for deduplication */
let lastTrackedUrl = '';

/**
 * Detect device type from User-Agent via lightweight regex (#765).
 * Matches Bowser's server-side classification for mobile/tablet/desktop.
 * Computed once — device doesn't change mid-session.
 * @returns {'mobile'|'tablet'|'desktop'}
 */
function detectDeviceType() {
  const ua = navigator.userAgent;
  if (/iPad|Android(?!.*Mobile)|Tablet/i.test(ua)) return 'tablet';
  if (/Mobile|iPhone|iPod|Android.*Mobile|webOS|BlackBerry|Opera Mini|IEMobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

/** @type {string} Device type — computed once at module load */
const detectedDeviceType = detectDeviceType();

/**
 * Initialize the analytics beacon.
 * Call once during widget mount.
 *
 * @param {Object} options
 * @param {string} options.apiUrl - Base API URL (e.g. 'https://app.shiftgears.ai')
 * @param {string} options.frontendToken - Frontend access token for auth
 * @param {string} options.visitorId - Persistent visitor identifier
 * @param {string} [options.sessionId] - Dialog session ID (may be null initially)
 */
export function initBeacon(options) {
  if (initialized) {
    console.warn('[AnalyticsBeacon] Already initialized, skipping');
    return;
  }

  apiUrl = options.apiUrl;
  frontendToken = options.frontendToken;
  visitorId = options.visitorId;
  currentSessionId = options.sessionId || null;

  if (!apiUrl || !frontendToken || !visitorId) {
    console.warn('[AnalyticsBeacon] Missing required config, beacon disabled', {
      apiUrl: !!apiUrl,
      frontendToken: !!frontendToken,
      visitorId: !!visitorId,
    });
    return;
  }

  initialized = true;

  // Periodic flush
  flushIntervalId = setInterval(flushBuffer, FLUSH_INTERVAL_MS);

  // Flush on page hide (tab switch, minimize, navigation away)
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  // SPA navigation tracking
  setupNavigationTracking();

  console.debug('[AnalyticsBeacon] Initialized', {
    apiUrl,
    visitorId,
    sessionId: currentSessionId,
  });
}

/**
 * Update session ID when dialog starts.
 * @param {string} sessionId
 */
export function setBeaconSessionId(sessionId) {
  currentSessionId = sessionId;
}

/**
 * Track an analytics event.
 * Events are buffered and sent in batches.
 *
 * @param {string} eventName - Event name (e.g. 'widget_open', 'page_view')
 * @param {Object} [params={}] - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  if (!initialized) return;

  eventBuffer.push({
    event: eventName,
    // `browsingSessionId` (#1467 / #1400 A1) is stamped per EVENT, not on the
    // envelope, so it needs no change on the API side — the controller spreads
    // `...event.params` straight into the stored jsonb. It is a real per-visit
    // id for every visitor; the envelope's `sessionId` remains the DIALOG id,
    // which only exists once a conversation starts.
    params: {
      ...params,
      device_type: detectedDeviceType,
      browsingSessionId: getOrCreateBrowsingSessionId(),
    },
    timestamp: Date.now(),
  });

  // Auto-flush if buffer is full
  if (eventBuffer.length >= MAX_BUFFER_SIZE) {
    flushBuffer();
  }
}

/**
 * Track a page view with URL, referrer, and UTM parameters.
 */
export function trackPageView() {
  if (!initialized) return;

  const url = window.location.href;

  // Deduplicate consecutive same-URL page views
  if (url === lastTrackedUrl) return;
  lastTrackedUrl = url;

  const params = {
    url,
    path: window.location.pathname,
    hash: window.location.hash,
    referrer: document.referrer || null,
    title: document.title || null,
  };

  // Extract UTM parameters
  const searchParams = new URLSearchParams(window.location.search);
  const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  for (const key of utmKeys) {
    const value = searchParams.get(key);
    if (value) {
      params[key] = value;
    }
  }

  // Screen dimensions
  params.screen_width = window.screen.width;
  params.screen_height = window.screen.height;
  params.viewport_width = window.innerWidth;
  params.viewport_height = window.innerHeight;

  // Language and timezone (#753)
  params.language = navigator.language || null;
  params.languages = navigator.languages ? navigator.languages.join(',') : null;
  params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;

  trackEvent('page_view', params);
}

/**
 * Destroy the beacon. Flushes remaining events and cleans up listeners.
 * Call on widget unmount.
 */
export function destroyBeacon() {
  if (!initialized) return;

  // Flush remaining events
  flushBuffer();

  // Cleanup
  if (flushIntervalId !== null) {
    clearInterval(flushIntervalId);
    flushIntervalId = null;
  }

  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('pagehide', onPageHide);
  teardownNavigationTracking();

  initialized = false;
  eventBuffer = [];

  console.debug('[AnalyticsBeacon] Destroyed');
}

// ─── Internal ───────────────────────────────────────────────

function onVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    flushBuffer();
  }
}

function onPageHide() {
  flushBuffer();
}

/**
 * Flush the event buffer to the backend.
 * Uses sendBeacon for reliability during page unload.
 */
function flushBuffer() {
  if (eventBuffer.length === 0) return;

  const events = eventBuffer.splice(0); // Take all events, clear buffer

  const payload = JSON.stringify({
    token: frontendToken,
    visitorId,
    sessionId: currentSessionId,
    events,
  });

  // Strip trailing /api to avoid double /api (same fix as serviceApi.js)
  const url = `${apiUrl.replace(/\/api\/?$/, '')}${ENDPOINT_PATH}`;

  // Try sendBeacon first (most reliable for unload scenarios)
  if (typeof navigator.sendBeacon === 'function') {
    const sent = navigator.sendBeacon(url, payload);
    if (sent) return;
    // If sendBeacon failed (e.g. payload too large), fall through to fetch
  }

  // Fallback: fetch with keepalive
  // Use text/plain to avoid CORS preflight on client sites
  // (application/json triggers OPTIONS preflight which our CORS whitelist may reject)
  try {
    fetch(url, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'text/plain' },
      keepalive: true,
    }).catch(() => {
      // Analytics should never crash the widget
    });
  } catch {
    // Silently fail — analytics is non-critical
  }
}

// ─── SPA Navigation Tracking ───────────────────────────────

function setupNavigationTracking() {
  // Listen for hash changes (our widget uses hash routing: #/cars, #/car/:id)
  window.addEventListener('hashchange', onNavigationChange);

  // Listen for popstate (browser back/forward)
  window.addEventListener('popstate', onNavigationChange);

  // NOTE: We intentionally do NOT monkey-patch pushState/replaceState.
  // It conflicts with client's SPA routers (React Router, Vue Router, etc.)
  // and our teardown would destroy their patches.
  // hashchange + popstate covers our widget navigation and browser back/forward.
  // Client SPA navigation tracking via Navigation API can be added in Phase 3.
}

function teardownNavigationTracking() {
  window.removeEventListener('hashchange', onNavigationChange);
  window.removeEventListener('popstate', onNavigationChange);
}

function onNavigationChange() {
  // Small delay to let the URL update
  setTimeout(() => trackPageView(), 0);
}
